import React, { useState, useRef, useCallback } from 'react';
import Image from '../../../components/AppImage';

// Handcrafted SVG icons
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="13" height="13" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M16 3l5 5-9 9H7v-5L16 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 5l5 5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M4 7h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M10 7V4h4v3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M6 7l1.5 13h9L18 7" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
  </svg>
);

const ZapIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="currentColor"/>
  </svg>
);

const LeafIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 0 0 1.38 1.38C8.55 18.9 14.9 16.8 17 8z" fill="currentColor"/>
    <path d="M3 21l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
  </svg>
);

const WindIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M9.59 4.59A2 2 0 1 1 11 8H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M12.59 19.41A2 2 0 1 0 14 16H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M6.91 12.91A2 2 0 1 0 8 16H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
  </svg>
);

const CarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M5 17H3a2 2 0 0 1-2-2V9l3-6h14l3 6v6a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="7.5" cy="17.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="16.5" cy="17.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const PercentIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <circle cx="6.5" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="17.5" cy="17.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const VehicleCard = ({ vehicle, onEdit, onDelete, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [accentTransition, setAccentTransition] = useState(false);
  const cardRef = useRef(null);
  const animFrameRef = useRef(null);

  const emissionConfig = {
    ev:       { label: 'EL',  Icon: ZapIcon,  color: '#2EC4A0', bg: 'rgba(46,196,160,0.08)',  border: '#2EC4A0', tape: 'rgba(46,196,160,0.5)' },
    hybrid:   { label: 'HY', Icon: LeafIcon, color: '#2EC4A0', bg: 'rgba(46,196,160,0.08)',  border: '#2EC4A0', tape: 'rgba(46,196,160,0.5)' },
    euro6:    { label: 'E6', Icon: WindIcon, color: '#F5A623', bg: 'rgba(245,166,35,0.08)',  border: '#F5A623', tape: 'rgba(245,166,35,0.5)' },
    standard: { label: 'ST', Icon: CarIcon,  color: '#8A95A3', bg: 'rgba(138,149,163,0.06)', border: 'rgba(138,149,163,0.4)', tape: 'rgba(138,149,163,0.3)' }
  };

  const emission = emissionConfig?.[vehicle?.emissionClassification] || emissionConfig?.standard;

  // Alternating orange/teal border accent — transitions to opposite on hover
  const baseAccent = index % 2 === 0 ? '#F5A623' : '#2EC4A0';
  const hoverAccent = index % 2 === 0 ? '#2EC4A0' : '#F5A623';
  const accentColor = isHovered ? hoverAccent : baseAccent;

  // Scrapbook rotation
  const rotations = [-1.5, 1.2, -0.8, 1.6, -1.0];
  const rotation = rotations?.[index % rotations?.length];

  const handleMouseMove = useCallback((e) => {
    if (!cardRef?.current) return;
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
    animFrameRef.current = requestAnimationFrame(() => {
      const rect = cardRef?.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect?.left + rect?.width / 2;
      const cy = rect?.top + rect?.height / 2;
      const dx = (e?.clientX - cx) / (rect?.width / 2);
      const dy = (e?.clientY - cy) / (rect?.height / 2);
      setTilt({ x: dy * -7, y: dx * 7 });
    });
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setAccentTransition(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setAccentTransition(false);
    setTilt({ x: 0, y: 0 });
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
  };

  const transform = isHovered
    ? `rotate(0deg) translateY(-12px) scale(1.03) rotateX(${tilt?.x}deg) rotateY(${tilt?.y}deg)`
    : `rotate(${rotation}deg) rotateX(0deg) rotateY(0deg)`;

  return (
    <div
      ref={cardRef}
      style={{
        position: 'relative',
        background: '#ECEEF0',
        padding: '8px 8px 22px',
        transform,
        transformStyle: 'preserve-3d',
        perspective: '800px',
        boxShadow: isHovered
          ? `6px 6px 0 ${accentColor}, 14px 14px 40px rgba(0,0,0,0.55)`
          : `4px 4px 0 rgba(0,0,0,0.45), 8px 8px 20px rgba(0,0,0,0.35)`,
        transition: 'transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 380ms cubic-bezier(0.34, 1.56, 0.64, 1), border-top-color 300ms ease',
        cursor: 'default',
        borderTop: `4px solid ${accentColor}`,
        zIndex: isHovered ? 10 : 1
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {/* Tape corners — scrapbook aesthetic */}
      <div style={{
        position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
        width: 52, height: 14, background: `${accentColor}55`,
        opacity: 0.9, zIndex: 5,
        transition: 'background 300ms ease'
      }} />
      {/* Primary ribbon */}
      {vehicle?.isPrimary && (
        <div style={{
          position: 'absolute',
          top: '16px', right: '-26px',
          background: '#F5A623',
          color: '#0A0C10',
          padding: '4px 34px',
          transform: 'rotate(35deg)',
          fontFamily: 'Impact, Haettenschweiler, sans-serif',
          fontSize: '0.5rem',
          fontWeight: 900,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          zIndex: 10,
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
        }}>PRIMÆR</div>
      )}
      {/* Vehicle image */}
      <div style={{ height: '158px', overflow: 'hidden', position: 'relative' }}>
        <Image
          src={vehicle?.image}
          alt={vehicle?.imageAlt || `${vehicle?.make} ${vehicle?.model}`}
          className="w-full h-full object-cover"
          style={{
            filter: 'brightness(0.82) contrast(1.08) saturate(1.1)',
            transform: isHovered ? 'scale(1.07)' : 'scale(1)',
            transition: 'transform 480ms cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
        {/* Emission stamp — bottom left */}
        <div style={{
          position: 'absolute',
          bottom: '8px', left: '8px',
          background: '#0A0C10',
          border: `2px solid ${emission?.color}`,
          padding: '4px 10px',
          display: 'flex', alignItems: 'center', gap: '5px',
          color: emission?.color
        }}>
          <emission.Icon />
          <span style={{
            fontFamily: 'Impact, Haettenschweiler, sans-serif',
            fontSize: '0.625rem',
            fontWeight: 900,
            letterSpacing: '0.1em'
          }}>{emission?.label}</span>
        </div>
        {/* Scrapbook corner stamp — top right */}
        <div style={{
          position: 'absolute', top: 8, right: 8,
          width: 28, height: 28,
          border: `2px solid ${accentColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(10,12,16,0.7)',
          transition: 'border-color 300ms ease'
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill={accentColor} />
          </svg>
        </div>
      </div>
      {/* Polaroid caption area */}
      <div style={{ padding: '14px 8px 4px', background: '#ECEEF0' }}>
        {/* Vehicle name */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{
            fontFamily: 'Impact, Haettenschweiler, "Arial Narrow", sans-serif',
            fontSize: '1.3rem',
            fontWeight: 900,
            color: '#0A0C10',
            letterSpacing: '-0.02em',
            lineHeight: 1,
            textTransform: 'uppercase'
          }}>{vehicle?.make}</div>
          <div style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: '0.9rem',
            color: '#555',
            marginTop: '2px'
          }}>{vehicle?.model}</div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{
            fontFamily: '"Courier New", monospace',
            fontSize: '0.7rem',
            fontWeight: 700,
            background: '#0A0C10',
            color: '#ECEEF0',
            padding: '2px 8px',
            letterSpacing: '0.06em'
          }}>{vehicle?.registrationNumber}</span>
          <span style={{
            fontFamily: 'Georgia, serif',
            fontSize: '0.8rem',
            color: '#777',
            fontStyle: 'italic'
          }}>{vehicle?.year}</span>
        </div>

        {/* Discount badge */}
        {vehicle?.discountEligible && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            marginBottom: '10px',
            color: accentColor,
            transition: 'color 300ms ease'
          }}>
            <PercentIcon />
            <span style={{
              fontFamily: 'Impact, Haettenschweiler, sans-serif',
              fontSize: '0.7rem',
              fontWeight: 900,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'inherit'
            }}>{vehicle?.discountPercentage} rabatt på bom</span>
          </div>
        )}

        {/* Handcrafted dashed divider */}
        <div style={{
          height: '1px',
          background: `repeating-linear-gradient(90deg, ${accentColor} 0px, ${accentColor} 4px, transparent 4px, transparent 9px)`,
          opacity: 0.4,
          marginBottom: '12px',
          transition: 'background 300ms ease'
        }} />

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={() => onEdit(vehicle?.id)}
            style={{
              flex: 1,
              background: '#0A0C10',
              border: `1px solid ${accentColor}40`,
              color: '#ECEEF0',
              padding: '8px 10px',
              fontFamily: 'Impact, Haettenschweiler, sans-serif',
              fontSize: '0.625rem',
              fontWeight: 900,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 150ms ease',
              boxShadow: `2px 2px 0 ${accentColor}40`
            }}
            onMouseEnter={e => { e.currentTarget.style.background = accentColor; e.currentTarget.style.color = '#0A0C10'; e.currentTarget.style.transform = 'translate(-1px,-1px)'; e.currentTarget.style.boxShadow = `3px 3px 0 ${accentColor}60`; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#0A0C10'; e.currentTarget.style.color = '#ECEEF0'; e.currentTarget.style.transform = 'translate(0,0)'; e.currentTarget.style.boxShadow = `2px 2px 0 ${accentColor}40`; }}
          >
            <EditIcon />
            Rediger
          </button>
          <button
            onClick={() => onDelete(vehicle?.id)}
            style={{
              width: '34px', height: '34px',
              background: 'transparent',
              border: '1px solid rgba(232,85,85,0.35)',
              color: '#E85555',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 150ms ease',
              flexShrink: 0,
              boxShadow: '2px 2px 0 rgba(232,85,85,0.15)'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#E85555'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translate(-1px,-1px)'; e.currentTarget.style.boxShadow = '3px 3px 0 rgba(232,85,85,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#E85555'; e.currentTarget.style.transform = 'translate(0,0)'; e.currentTarget.style.boxShadow = '2px 2px 0 rgba(232,85,85,0.15)'; }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleCard;